import { useRef, useState, useEffect } from 'react';
import htmlDocx from 'html-docx-js/dist/html-docx';
import html2pdf from 'html2pdf.js';
import { FaFileWord, FaFilePdf } from 'react-icons/fa';
import { Dropdown } from 'primereact/dropdown';
import { getRequest } from '../services/apiService';
import EquipmentStatusTable from './tables/EquipmentStatusTable';
import ShipyardCommentsTable from './tables/ShipyardCommentsTable';
import EnclosuresList from './tables/EnclosuresList';

const TableComponent = ({ title, headers, headerStructure, data, contractInfo }) => {
  const hasComplexHeader = headerStructure && headerStructure.length > 0;

  return (
    <div className="my-6">
      {title && <h4 className="underline p-4 text-lg font-semibold">{title}</h4>}
      <table className="w-full border-collapse border border-gray-400">
        <thead>
          {contractInfo && (
            <tr>
              <th
                colSpan={headers.length}
                className="border border-gray-400 p-2 text-left bg-gray-100"
              >
                {contractInfo}
              </th>
            </tr>
          )}

          {hasComplexHeader && (
            <tr>
              {headerStructure.map((header, index) => (
                <th
                  key={`complex-header-${index}`}
                  colSpan={header.colSpan || 1}
                  rowSpan={header.rowSpan || 1}
                  className="border border-gray-400 p-2 text-center bg-gray-100"
                >
                  {header.text}
                </th>
              ))}
            </tr>
          )}

          <tr>
            {headers.map((header, index) => (
              <th
                key={`header-${index}`}
                className={`border border-gray-400 p-2 ${
                  hasComplexHeader && headerStructure.some(h =>
                    h.rowSpan > 1 && index >= (h.startIndex || 0) && index < (h.startIndex || 0) + h.colSpan
                  ) ? 'hidden' : ''
                }`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="border border-gray-400 p-2">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyProgressReport = () => {
  const reportRef = useRef();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [months] = useState([
    { id: 1, name: 'January' },
    { id: 2, name: 'February' },
    { id: 3, name: 'March' },
    { id: 4, name: 'April' },
    { id: 5, name: 'May' },
    { id: 6, name: 'June' },
    { id: 7, name: 'July' },
    { id: 8, name: 'August' },
    { id: 9, name: 'September' },
    { id: 10, name: 'October' },
    { id: 11, name: 'November' },
    { id: 12, name: 'December' }
  ]);
  const [selectedMonth, setSelectedMonth] = useState({ id: new Date().getMonth() + 1, name: months[new Date().getMonth()].name });
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchAllProjects = async () => {
    let allProjects = [];
    let nextUrl = 'master/projects/';
    
    try {
      setLoading(true);
      setError(null);
      
      while (nextUrl) {
        const response = await getRequest(nextUrl);
        
        if (response?.status === 'success') {
          allProjects = [...allProjects, ...response.data];
          nextUrl = response.next;
        } else {
          break;
        }
      }
      
      setProjects(allProjects);
      if (allProjects.length > 0) {
        setSelectedProject(allProjects[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProjects();
  }, []);

  useEffect(() => {
    if (selectedProject?.id && selectedMonth && year) {
      const fetchReportData = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await getRequest(
            `transaction/report/?project_id=${selectedProject.id}&month=${selectedMonth.id}&year=${year}`
          );
          setReportData(response);
        } catch (error) {
          console.error('Error fetching report data:', error);
          setError('Failed to load report data. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      fetchReportData();
    }
  }, [selectedProject, selectedMonth, year]);

  const handleWordExport = () => {
    const content = reportRef.current;
    const htmlString = content.innerHTML;

    const docx = htmlDocx.asBlob(htmlString, {
      orientation: 'portrait',
      margins: { top: 1000, right: 1000, bottom: 1000, left: 1000 }
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(docx);
    link.download = 'monthlyprogressreport.docx';
    link.click();
  };

  const handlePdfExport = () => {
    const element = reportRef.current;
    html2pdf().from(element).save();
  };

  const getContractualTimelinesHeaders = () => {
    if (!reportData?.contractual_timelines?.[0]) return [];
    const headers = reportData.contractual_timelines[0];
    return Object.values(headers);
  };

  const getContractualTimelinesData = () => {
    if (!reportData?.contractual_timelines || reportData.contractual_timelines.length < 2) return [];
    
    const headers = reportData.contractual_timelines[0];
    return reportData.contractual_timelines.slice(1).map(row => {
      return Object.keys(headers).map(key => row[key] || '');
    });
  };

  const getProcurementSummaryHeaders = () => {
    if (!reportData?.procurement_status?.summary?.[0]) return [];
    const headers = reportData.procurement_status.summary[0];
    return Object.values(headers);
  };
  
  const getProcurementSummaryData = () => {
    if (!reportData?.procurement_status?.summary || reportData.procurement_status.summary.length < 2) return [];
    
    return reportData.procurement_status.summary.slice(1).map(row => {
      return Object.values(row);
    });
  };
  
  const getDepartmentProcurementData = (department) => {
    const deptData = reportData?.procurement_status?.departments?.find(
      d => d.department.toLowerCase() === department.toLowerCase()
    );
    
    if (!deptData?.data || deptData.data.length < 2) return { headers: [], data: [] };
    
    const headers = Object.values(deptData.data[0]);
    const data = deptData.data.slice(1).map(row => Object.values(row));
    
    return { headers, data };
  };

  const getPipingProgressData = () => {
    if (!reportData?.physical_progress) return [];
    
    const pipingData = reportData.physical_progress.find(item => item.Task === 'Piping');
    if (!pipingData?.Subtasks) return [];
    
    // Group subtasks by system
    const systems = {};
    pipingData.Subtasks.forEach(subtask => {
      const systemName = subtask.Name.split(' - ')[0];
      const progressType = subtask.Name.split(' - ')[1];
      
      if (!systems[systemName]) {
        systems[systemName] = {
          name: systemName,
          fabrication: {},
          installation: {},
          ii: {},
          pt: {}
        };
      }
      
      if (progressType === 'Fabrication') {
        systems[systemName].fabrication = {
          mp: subtask['Monthly Planned Progress %'],
          cp: subtask['Cumulative Planned Progress %'],
          ma: subtask['Actual Progress %'],
          ca: subtask['Cumulative Actual Progress %']
        };
      } else if (progressType === 'Installation') {
        systems[systemName].installation = {
          mp: subtask['Monthly Planned Progress %'],
          cp: subtask['Cumulative Planned Progress %'],
          ma: subtask['Actual Progress %'],
          ca: subtask['Cumulative Actual Progress %']
        };
      } else if (progressType === 'II') {
        systems[systemName].ii = {
          mp: subtask['Monthly Planned Progress %'],
          cp: subtask['Cumulative Planned Progress %'],
          ma: subtask['Actual Progress %'],
          ca: subtask['Cumulative Actual Progress %']
        };
      } else if (progressType === 'Pressure Test') {
        systems[systemName].pt = {
          mp: subtask['Monthly Planned Progress %'],
          cp: subtask['Cumulative Planned Progress %'],
          ma: subtask['Actual Progress %'],
          ca: subtask['Cumulative Actual Progress %']
        };
      }
    });
    
    // Convert to table rows
    return Object.values(systems).map((system, index) => [
      index + 1,
      '', // PBS - not in API data
      system.name,
      '', // Total Drg. Rcvd - not in API data
      system.fabrication.mp || '',
      system.fabrication.cp || '',
      system.fabrication.ma || '',
      system.fabrication.ca || '',
      system.installation.mp || '',
      system.installation.cp || '',
      system.installation.ma || '',
      system.installation.ca || '',
      system.ii.mp || '',
      system.ii.cp || '',
      system.ii.ma || '',
      system.ii.ca || '',
      system.pt.mp || '',
      system.pt.cp || '',
      system.pt.ma || '',
      system.pt.ca || ''
    ]);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.value);
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };
  
  const getHullStructureData = () => {
    const hullTask = reportData?.physical_progress?.find(item => item.Task === 'Hull');
    if (!hullTask || !hullTask.Subtasks) return [];
  
    return hullTask.Subtasks.map(subtask => [
      subtask.Name, // Activity Name
      subtask['Total Units'] || '', // Changed from subtask.TotalUnits to access the correct property
      '', // Placeholder for Apprx. Ton
      '', // Placeholder for Unit
      ''  // Placeholder for Ton
    ]);
  };
  const getHullOutfitData = () => {
    const hullOutfitTask = reportData?.physical_progress?.find(item => item.Task === 'Hull Outfit');
    if (!hullOutfitTask || !hullOutfitTask.Subtasks) return [];
    
    // Map each subtask to a row in the table
    return hullOutfitTask.Subtasks.map((subtask, index) => [
      index + 1, // Sr. number
      subtask.Name, // Group name
      '', // Qty. per ship (Nos.) - not in API data
      subtask['Monthly Planned Progress %'] || '', // MP
      subtask['Cumulative Planned Progress %'] || '', // CP
      subtask['Actual Progress %'] || '', // MA
      subtask['Cumulative Actual Progress %'] || '' // CA
    ]);
  };
  const getS3StageData = () => {
    const trunkingTask = reportData?.physical_progress?.find(
      item => item.Task === "Trunking and Ducting"
    );
    const s3Stage = trunkingTask?.Stages?.find(stage => stage.Stage === "S3");
    
    return s3Stage?.Units?.map((unit, index) => [
      index + 1,
      unit.Unit,
      unit["Pallet ID"],
      '', // Drg Rcvd (m) - not in API
      '', // Fabrication MP
      '', // Fabrication CP
      '', // Fabrication MA
      '', // Fabrication CA
      '', // Installation MP
      '', // Installation CP
      '', // Installation MA
      '', // Installation CA
    ]) || [];
  };

  const getPIStatusData = () => {
    const inspectionTask = reportData?.physical_progress?.find(
      item => item.Task === "Compartment Inspection"
    );
    
    return inspectionTask?.Subtasks?.map(subtask => [
      subtask.Name,
      subtask["Total Compartments"],
      subtask.Planned,
      subtask.Conducted,
      subtask.SAT
    ]) || [];
  };
  return (
    <div className="p-5 relative" ref={reportRef}>
      <div className="absolute right-5 top-5 flex gap-3">
        <button
          onClick={handleWordExport}
          className="bg-none border-none cursor-pointer"
          title="Download Word Document"
        >
          <FaFileWord size={32} color="#2B5797" />
        </button>

        <button
          onClick={handlePdfExport}
          className="bg-none border-none cursor-pointer"
          title="Download PDF Document"
        >
          <FaFilePdf size={32} color="#B30B00" />
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center mb-5 gap-4">
        <div className="w-full md:w-72">
          {loading ? (
            <p className="text-gray-600">Loading projects...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <Dropdown
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.value)}
              options={projects}
              optionLabel="name"
              placeholder="Select a Project"
              className="w-full"
            />
          )}
        </div>
          <div className="w-full md:w-48">
            <Dropdown
              value={selectedMonth}
              onChange={handleMonthChange}
              options={months}
              optionLabel="name"
              placeholder="Select Month"
              className="w-full"
            />
          </div>

          <div className="w-full md:w-32">
            <input
              type="number"
              value={year}
              onChange={handleYearChange}
              placeholder="Year"
              className="w-full p-2 border border-gray-300 rounded"
              min="2000"
              max="2100"
            />
          </div>

        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
        </div>
      </div>

      <h1 className="underline text-center font-bold text-2xl pt-3">MONTHLY PROGRESS REPORT</h1>
      <h2 className="underline text-center font-bold text-2xl pt-8">
        {reportData?.project_details?.name || (selectedProject ? selectedProject.name.toUpperCase() : 'PROJECT-17A')}
      </h2>
  
      <EnclosuresList yards={reportData?.project_details?.yards || []} />

      {reportData?.contractual_timelines && (
        <TableComponent
          title="1. Contractual Timelines"
          contractInfo={`The Contract was signed on __________ to build ${reportData?.project_details?.yards?.length || 0} ship(s).`}
          headers={getContractualTimelinesHeaders()}
          data={getContractualTimelinesData()}
        />
      )}

      <ShipyardCommentsTable/>

      <h3 className='font-bold pb-4'>A. <u>REPORT ON YARD</u> <u className=''><span className='text-green-600'>A, B, C & D</span></u></h3>
      <h3 className='font-bold pl-5 pb-4'>1.	TIMELINES <u>OF A</u>:</h3>
      <h3 className='font-bold pl-5'>2.	PHYSICAL <u>PROGRESS</u></h3>

      <TableComponent
        title="a) Physical Progress (A)"
        headers={['', 'MP', 'CP', 'MA', 'CA']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Sr.' },
          { colSpan: 1, rowSpan: 2, text: 'Group' },
          { colSpan: 1, rowSpan: 2, text: 'Total' },
          { colSpan: 4, rowSpan: 1, text: 'Up to (Month, Year)' },
        ]}
        data={[
          ['i', 'Hull Structure', '19.5', '', '', '', ''],
          ['ii', 'Hull Outfit', '30.5', '', '', '', ''],
          ['iii', 'Piping', '13.0', '', '', '', ''],
          ['iv', 'Engineering', '10.0', '', '', '', ''],
          ['v', 'Electrical', '17.17', '', '', '', ''],
          ['vi', 'Weapon', '6.33', '', '', '', ''],
          ['vii', 'Dry dock & Trials', '3.5', '', '', '', ''],
          ['', 'Total', '100', '', '', '', '']
        ]}
      />

<TableComponent
  title="b) Hull Structure"
  headers={['', 'Unit', 'Apprx. Ton', 'Unit', 'Ton']}
  headerStructure={[
    { colSpan: 1, rowSpan: 2, text: 'ACTIVITY' },
    { colSpan: 2, rowSpan: 1, text: 'Total' },
    { colSpan: 2, rowSpan: 1, text: 'ACTUAL FABRICATED AND ERECTED' }
  ]}
  data={getHullStructureData()} // Use the new function to get data
/>

<TableComponent
  title="c)	Status of Hull outfit "
  headers={['', 'MP', 'CP', 'MA', 'CA']}
  headerStructure={[
    { colSpan: 1, rowSpan: 2, text: 'Sr.' },
    { colSpan: 1, rowSpan: 2, text: 'Group' },
    { colSpan: 1, rowSpan: 2, text: 'Qty. per ship (Nos.)' },
    { colSpan: 4, rowSpan: 1, text: '(Month, Year)' }
  ]}
  data={getHullOutfitData()}
/>
      CP: Cumulative Plan, CA: Cumulative Actual

      {reportData?.physical_progress?.find(item => item.Task === 'Piping') && (
        <TableComponent
          title="d)	Piping Progress-System wise (Nos)"
          headers={['', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA']}
          headerStructure={[
            { colSpan: 1, rowSpan: 2, text: 'Sr.' },
            { colSpan: 1, rowSpan: 2, text: 'PBS' },
            { colSpan: 1, rowSpan: 2, text: 'System' },
            { colSpan: 1, rowSpan: 2, text: 'Total Drg. Rcvd' },
            { colSpan: 4, rowSpan: 1, text: 'FABRICATION' },
            { colSpan: 4, rowSpan: 1, text: 'INSTALLATION' },
            { colSpan: 4, rowSpan: 1, text: 'II' },
            { colSpan: 4, rowSpan: 1, text: 'PT' },
          ]}
          data={getPipingProgressData()}
        />
      )}

      <h3>e) Trunking and ducting Progress</h3>

      <TableComponent
  title={`i. S3 Stage Pre-outfitting (D0 (Pallet for fabrication) & DP (Pallet for installation))`}
  headers={['', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA']}
  headerStructure={[
    { colSpan: 1, rowSpan: 2, text: 'Sr.' },
    { colSpan: 1, rowSpan: 2, text: 'Unit' },
    { colSpan: 1, rowSpan: 2, text: 'Pallet ID' },
    { colSpan: 1, rowSpan: 2, text: 'Drg Rcvd (m)' },
    { colSpan: 4, rowSpan: 1, text: 'FABRICATION' },
    { colSpan: 4, rowSpan: 1, text: 'INSTALLATION' },
  ]}
  data={getS3StageData()}
/>

      <TableComponent
        title="ii.	S5 Stage outfitting (D0 (Pallet for fabrication) & DP (Pallet for installation))"
        headers={['', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Sr.' },
          { colSpan: 1, rowSpan: 2, text: 'Unit' },
          { colSpan: 1, rowSpan: 2, text: 'Pallet ID' },
          { colSpan: 1, rowSpan: 2, text: 'Drg Rcvd (m)' },
          { colSpan: 4, rowSpan: 1, text: 'FABRICATION' },
          { colSpan: 4, rowSpan: 1, text: 'INSTALLATION' },
        ]}
        data={[
          ['1', 'A2KAP', 'F3A2KXBD00', '', '', '', '', '', '', '', '', ''],
          ['2', 'A2KAC', '', '', '', '', '', '', '', '', '', ''],
          ['3', 'A2KAS', 'F3A1KXBD00', '', '', '', '', '', '', '', '', ''],
          ['4', 'A2KFP', '', '', '', '', '', '', '', '', '', ''],
          ['5', 'A2KFS', 'F3E1KXCD00', '', '', '', '', '', '', '', '', '']
        ]}
      />

      <h3>f) Electrical Outfitting Progress (L0)</h3>

      <TableComponent
        title="i.	Installation of Pre-Outfitting Elements"
        headers={['', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Sr.' },
          { colSpan: 1, rowSpan: 2, text: 'Pre-outfitting element' },
          { colSpan: 1, rowSpan: 2, text: 'Total nos.' },
          { colSpan: 4, rowSpan: 1, text: 'FABRICATION' },
          { colSpan: 4, rowSpan: 1, text: 'INSTALLATION' },
        ]}
        data={[
          ['1', 'Hanger', '', '', '', '', '', '', '', '', ''],
          ['2', 'Trays*', '', '', '', '', '', '', '', '', ''],
          ['3', 'Glands', '', '', '', '', '', '', '', '', ''],
          ['4', 'Slots', '', '', '', '', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="ii.	Cable Laying"
        headers={['', 'MP', 'CP', 'MA', 'CA']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Sr.' },
          { colSpan: 1, rowSpan: 2, text: 'Pre-outfitting element' },
          { colSpan: 1, rowSpan: 2, text: 'Total (Km)' },
          { colSpan: 4, rowSpan: 1, text: 'Laying (Km)' },
        ]}
        data={[
          ['1', 'Main Run cable', '', '', '', '', ''],
          ['2', 'Separate Run Cable', '', '', '', '', ''],
          ['3', 'Local Run cable', '', '', '', '', '',],
        ]}
      />

      <TableComponent
        title="g)	Progress of major seats S5 Stage fabrication and outfitting of seats (Hull Outfit (SS), Engineering (SE), Electrical (SL) & Weapon (SW)"
        headers={['', 'MP', 'CP', 'MA', 'CA', 'MP', 'CP', 'MA', 'CA']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Sr.' },
          { colSpan: 1, rowSpan: 2, text: 'BLOCK' },
          { colSpan: 1, rowSpan: 2, text: 'Pallet ID' },
          { colSpan: 1, rowSpan: 2, text: 'Drg Rcvd (Nos)' },
          { colSpan: 4, rowSpan: 1, text: 'FABRICATION' },
          { colSpan: 4, rowSpan: 1, text: 'INSTALLATION' },
        ]}
        data={[
          ['1', 'A2KXB', '', '', '', '', '', '', '', '', '', ''],
          ['2', 'A3KXB', '', '', '', '', '', '', '', '', '', ''],
          ['3', 'A1KXB', '', '', '', '', '', '', '', '', '', ''],
          ['4', 'B2KXB', '', '', '', '', '', '', '', '', '', ''],
          ['5', 'E1KXC', '', '', '', '', '', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="i.	Status of Major Seats (For monitoring physical Progress):"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'SRL' },
          { colSpan: 1, rowSpan: 1, text: 'DESCRIPTION' },
          { colSpan: 1, rowSpan: 1, text: 'QTY.' },
          { colSpan: 1, rowSpan: 1, text: 'FAB.' },
          { colSpan: 1, rowSpan: 1, text: 'INST.' },
        ]}
        data={[
          ['1', 'GAS TURBINE', '', '', ''],
          ['2', 'DIESEL ENGINE', '', '', ''],
          ['3', 'STEERING GEAR', '', '', ''],
          ['4', 'THRUST BLOCK', '', '', ''],
          ['5', 'DIESEL ALTERNATOR', '', '', '']
        ]}
      />

<TableComponent
  title={`h) Status of Preliminary Inspection (PI) of Compartment (As on ${selectedMonth?.name} ${year})`}
  headers={['DESCRIPTION', 'QTY.', 'Planned', 'Conducted', 'SAT']}
  headerStructure={[
    { colSpan: 1, rowSpan: 1, text: 'SRL' },
    { colSpan: 1, rowSpan: 1, text: 'DESCRIPTION' },
    { colSpan: 1, rowSpan: 1, text: 'QTY.' },
    { colSpan: 1, rowSpan: 1, text: 'Planned' },
    { colSpan: 1, rowSpan: 1, text: 'Conducted' },
    { colSpan: 1, rowSpan: 1, text: 'SAT' }
  ]}
  data={getPIStatusData().map((row, index) => [index + 1, ...row])}
/>

      <TableComponent
        title="i)	HVAC Equipment Status:"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'SRL' },
          { colSpan: 1, rowSpan: 1, text: 'Equipment' },
          { colSpan: 1, rowSpan: 1, text: 'Nos' },
          { colSpan: 1, rowSpan: 1, text: 'Received' },
          { colSpan: 1, rowSpan: 1, text: 'Ship-in' },
        ]}
        data={[
          ['1', 'ATU', '', '', ''],
          ['2', 'AFU', '', '', ''],
          ['3', 'AFU FAN', '', '', ''],
          ['4', 'FCU', '', '', ''],
          ['5', 'HE', '', '', '']
        ]}
      />

      <TableComponent
        title="j)	Insulation Status:"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'SRL' },
          { colSpan: 1, rowSpan: 1, text: 'Insulation Type' },
          { colSpan: 1, rowSpan: 1, text: 'Approx. Total area (sq. m)' },
          { colSpan: 1, rowSpan: 1, text: 'MP' },
          { colSpan: 1, rowSpan: 1, text: 'CP' },
          { colSpan: 1, rowSpan: 1, text: 'MA' },
          { colSpan: 1, rowSpan: 1, text: 'CA' },
        ]}
        data={[
          ['1', 'A', '', '', '', '', ''],
          ['2', 'B', '', '', '', '', ''],
          ['3', 'E', '', '', '', '', ''],
          ['4', 'F', '', '', '', '', ''],
          ['5', 'G', '', '', '', '', ''],
          ['6', 'L', '', '', '', '', ''],
          ['7', 'N2', '', '', '', '', ''],
          ['8', 'V', '', '', '', '', ''],
          ['9', 'X', '', '', '', '', ''],
          ['', 'Total', '', '', '', '', '']
        ]}
      />

      <h3 style={{ textDecoration: 'underline' }}>k. Equipment installation Status (S5)</h3>

      <TableComponent
        title="i.	Hull Equipment Installation"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Sr.' },
          { colSpan: 1, rowSpan: 1, text: 'Equipment' },
          { colSpan: 1, rowSpan: 1, text: 'Total nos.' },
          { colSpan: 1, rowSpan: 1, text: 'Ship In' },
          { colSpan: 1, rowSpan: 1, text: 'Installation' },
          { colSpan: 1, rowSpan: 1, text: 'Installation inspection' },
          { colSpan: 1, rowSpan: 1, text: 'Setting to Work' },
        ]}
        data={[
          ['1', 'Store Lift', '', '', '', ''],
          ['2', 'Glass Window & Window Wiper', '', '', '', '', ''],
          ['3', 'Bower Anchor', '', '', '', '', ''],
          ['4', 'Anchor Chain Cable & Accessories', '', '', '', '', ''],
          ['5', 'Anchor Capstan', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="ii.	Engineering Equipment Installation (EE)"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Sr.' },
          { colSpan: 1, rowSpan: 1, text: 'Equipment' },
          { colSpan: 1, rowSpan: 1, text: 'Total nos.' },
          { colSpan: 1, rowSpan: 1, text: 'Ship In' },
          { colSpan: 1, rowSpan: 1, text: 'Chocking /Bolting' },
          { colSpan: 1, rowSpan: 1, text: 'Bolting Down Inspection' },
          { colSpan: 1, rowSpan: 1, text: 'Installation inspection' },
          { colSpan: 1, rowSpan: 1, text: 'Setting to Work' },
        ]}
        data={[
          ['1', 'Gas Turbines', '', '', '', '', ''],
          ['2', 'Gear Box', '', '', '', '', '', ''],
          ['3', 'Main Diesel Engines', '', '', '', '', '', ''],
          ['4', 'CPP, Shafting & Thrust Block', '', '', '', '', '', ''],
          ['5', 'FO Centrifuge &LO centrifuge', '', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="iii Electrical Equipment Installation (L)"
        headers={['', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)']}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'System' },
          { colSpan: 1, rowSpan: 1, text: 'Ship In' },
          { colSpan: 1, rowSpan: 1, text: 'Installation' },
          { colSpan: 1, rowSpan: 1, text: 'Connection' },
          { colSpan: 1, rowSpan: 1, text: 'Installation Inspection' },
          { colSpan: 1, rowSpan: 1, text: 'STW' }
        ]}
        data={[
          ['Main Switch Boards', '', '', '', ''],
          ['Converter Supply System (C &C and 10 KVA Converter)', '', '', '', '', ''],
          ['APMS', '', '', '', '', ''],
          ['Deguassing System', '', '', '', '', ''],
          ['Helo Stating System (40 KVA Converter & Helo starting and rectifier)', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="iv Weapon Equipment Installation (Wn)"
        headers={['System', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)', 'Cum Actual (%)']}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: '' },
          { colSpan: 1, rowSpan: 1, text: 'Ship In' },
          { colSpan: 1, rowSpan: 1, text: 'Installation' },
          { colSpan: 1, rowSpan: 1, text: 'Connection' },
          { colSpan: 1, rowSpan: 1, text: 'Installation Inspection' },
          { colSpan: 1, rowSpan: 1, text: 'STW' }
        ]}
        data={[
          ['LR SAM', '', '', '', ''],
          ['MF STAR', '', '', '', '', ''],
          ['Humsa NG', '', '', '', '', ''],
          ['IAC MOD C', '', '', '', '', ''],
          ['BrahMos', '', '', '', '', '']
        ]}
      />

      <TableComponent
        title="v. Summary of Equipment Ship-in for Aug-23:"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Sr.no' },
          { colSpan: 1, rowSpan: 1, text: 'Summary of Ship-In' },
          { colSpan: 1, rowSpan: 1, text: 'MP' },
          { colSpan: 1, rowSpan: 1, text: 'MA' },
          { colSpan: 1, rowSpan: 1, text: 'CP' },
          { colSpan: 1, rowSpan: 1, text: 'CA' }
        ]}
        data={[
          ['1', 'Hull', '', '', ''],
          ['2', 'Engineering', '', '', '', ''],
          ['3', 'Electrical', '', '', '', ''],
          ['4', 'Weapon', '', '', '', ''],
        ]}
      />

      <h3 className='text-right'>Enclosure 2</h3>
      <h3 className='text-center'><u>STATUS OF APPROVALS</u></h3>

      <TableComponent
        title="1. SOTR's"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Department' },
          { colSpan: 1, rowSpan: 1, text: 'Total' },
          { colSpan: 1, rowSpan: 1, text: "SOTR's Required from WDB" },
          { colSpan: 1, rowSpan: 1, text: "SOTR's Received from WDB" },
          { colSpan: 1, rowSpan: 1, text: 'Balance' },
{ colSpan: 1, rowSpan: 1, text: 'MDL Remarks' }
  ]}
  data={[
    ['Structural', '', '', '', ''],
    ['Outfit', '', '', '', '', ''],
    ['Engineering', '', '', '', '', ''],
    ['Electrical', '', '', '', '', ''],
    ['Weapon', '', '', '', '', ''],
  ]}
/>

      <TableComponent
        title="2.	DRAWING APPROVALS"
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Department' },
          { colSpan: 1, rowSpan: 1, text: 'Total' },
          { colSpan: 1, rowSpan: 1, text: 'DWG forwarded to WDB' },
          { colSpan: 1, rowSpan: 1, text: 'Approved by WDB' },
          { colSpan: 1, rowSpan: 1, text: 'Approval Awaited (WDB)' },
          { colSpan: 1, rowSpan: 1, text: 'Balance with MDL' },
          { colSpan: 1, rowSpan: 1, text: 'MDL Remarks' }
        ]}
        data={[
          ['Structural', '', '', '', '', ''],
          ['Outfit', '', '', '', '', '', ''],
          ['Engineering', '', '', '', '', '', ''],
          ['Electrical', '', '', '', '', '', ''],
          ['Weapon', '', '', '', '', '', ''],
        ]}
      />

<TableComponent
  title="3. TSP's"
  headers={[]}
  headerStructure={[
    { colSpan: 1, rowSpan: 1, text: 'Department' },
    { colSpan: 1, rowSpan: 1, text: 'Total' },
    { colSpan: 1, rowSpan: 1, text: "TSP's Forwarded to WDB" },    // Fixed - using double quotes
    { colSpan: 1, rowSpan: 1, text: "Approved by WDB" },
    { colSpan: 1, rowSpan: 1, text: 'Approval Awaited' },
    { colSpan: 1, rowSpan: 1, text: 'Balance with MDL' },
    { colSpan: 1, rowSpan: 1, text: 'MDL Remarks' }
  ]}
  data={[
    ['Structural', '', '', '', '', ''],
    ['Outfit', '', '', '', '', '', ''],
    ['Engineering', '', '', '', '', '', ''],
    ['Electrical', '', '', '', '', '', ''],
    ['Weapon', '', '', '', '', '', ''],
  ]}
/>

      <h3 className='text-right'>Enclosure 3</h3>
      <h3 className='text-center'><u>STATUS OF DEVELOPMENT OF 3DCAD MODEL AND LOI/ APPROVAL OF CLOs</u></h3>

      <TableComponent
        title=""
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 1, text: 'Ser' },
          { colSpan: 1, rowSpan: 1, text: 'CATEGORY' },
          { colSpan: 1, rowSpan: 1, text: 'TOTAL' },
          { colSpan: 1, rowSpan: 1, text: 'ISSUED TO WDB' },
          { colSpan: 1, rowSpan: 1, text: 'LOI CONDUCTED' },
          { colSpan: 1, rowSpan: 1, text: 'SAT' },
          { colSpan: 1, rowSpan: 1, text: 'MDL Remarks' }
        ]}
        data={[
          ['1', 'GEN MODULAR', '', '', '', ''],
          ['2', 'OUTFIT', '', '', '', '', ''],
          ['3', 'WET MODULAR', '', '', '', '', ''],
          ['4', 'GALLEY', '', '', '', '', ''],
          ['5', 'ENGG', '', '', '', '', ''],
          ['6', 'VIP MODULAR', '', '', '', '', ''],
          ['7', 'TANK', '', '', '', '', ''],
          ['8', 'Cold ROOM', '', '', '', '', ''],
          ['', 'Total', '', '', '', '', ''],
        ]}
      />

      <h3 className='text-right'>Enclosure 4</h3>
      <h3 className='text-center'><u>STATUS OF PROCUREMENT/ DELIVERY OF EQUIPMENT</u></h3>

      {reportData?.procurement_status?.summary && (
        <TableComponent
          title="1. SUMMARY"
          headers={getProcurementSummaryHeaders()}
          data={getProcurementSummaryData()}
        />
      )}

      {reportData?.procurement_status?.departments && (
        <TableComponent
          title="2. PROCUREMENT STATUS- HULL ITEMS:"
          headers={getDepartmentProcurementData('Hull').headers}
          data={getDepartmentProcurementData('Hull').data}
        />
      )}

      {reportData?.procurement_status?.departments && (
        <TableComponent
          title="3. PROCUREMENT STATUS- ENGINEERING ITEMS:"
          headers={getDepartmentProcurementData('Engineering').headers}
          data={getDepartmentProcurementData('Engineering').data}
        />
      )}

      {reportData?.procurement_status?.departments && (
        <TableComponent
          title="4. PROCUREMENT STATUS- ELECTRICAL ITEMS:"
          headers={getDepartmentProcurementData('Electrical').headers}
          data={getDepartmentProcurementData('Electrical').data}
        />
      )}

      {reportData?.procurement_status?.departments && (
        <TableComponent
          title="5. PROCUREMENT STATUS- WEAPON ITEMS:"
          headers={getDepartmentProcurementData('Weapon').headers}
          data={getDepartmentProcurementData('Weapon').data}
        />
      )}

      <EquipmentStatusTable 
        equipmentStatusData={reportData?.equipment_status} 
        yards={reportData?.project_details?.yards || []} 
      />
      
      <h3 className='text-right'>Enclosure 6</h3>
      <h3 className='text-center'><u>STEEL STATUS</u></h3>

      <TableComponent
        title=""
        headers={[]}
        headerStructure={[
          { colSpan: 1, rowSpan: 2, text: 'Yard' },
          { colSpan: 1, rowSpan: 1, text: 'Quantity Ordered (MT)' },
          { colSpan: 1, rowSpan: 1, text: 'Quantity Received (MT)' },
          { colSpan: 1, rowSpan: 1, text: 'Balance Quantity (MT)' },
          { colSpan: 1, rowSpan: 1, text: 'Remark' }
        ]}
        data={[
          ['A', '', '', '', ''],
          ['B', '', '', '', ''],
          ['C', '', '', '', ''],
          ['D', '', '', '', ''],
          ['P (ABOVE 50MM)', '', '', '', ''],
          ['Original PO Total Qty', '', '', '', ''],
          ['Additional Steel', '', '', '', ''],
          ['Additional Steel', '', '', '', ''],
          ['Additional Steel', '', '', '', ''],
          ['Additional PO Qty', '', '', '', ''],
          ['Total', '', '', '', ''],
        ]}
      />
    </div>
  );
};

export default MonthlyProgressReport;